import { useCallback, useEffect, useState } from 'react'
import { Box, Divider, Grid, Typography, Switch, FormControlLabel, IconButton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddCircleIcon from '@mui/icons-material/AddCircle'

/** API */
import MemberRankApi, {
  RankConditionInterface,
  CalculationSettingsInterface,
  RANK_CONDITION_TYPE,
  RANK_PERIOD_TYPE,
  CONDITION_OPERATOR,
  CALCULATION_MODE
} from 'api/setting/member-rank.api'

/** COMPONENT */
import ButtonCustom from 'component/Button/ButtonCustom'
import InputCheckbox from 'component/Input/InputCheckbox'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import AutocompleteSelect from 'component/Select/AutocompleteSelect'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import PageModal from 'new-components/modal/page-modal'
import TextInput from 'new-components/inputs/text-input'
import SelectInput from 'new-components/inputs/select-input'

/** STYLE */
import { colors } from 'constants/theme'

interface RankUpdateSettingsProps {
  ranks: any[]
  onSettingsChange?: () => void
}

export default function RankUpdateSettings(props: RankUpdateSettingsProps) {
  const { ranks, onSettingsChange } = props
  const { t } = useTranslation()

  // Settings state
  const [settings, setSettings] = useState<CalculationSettingsInterface>({
    calculationMode: CALCULATION_MODE.REALTIME,
    calculationTime: '08:00',
    includeOverdue: false,
    includeRefund: false,
    includeTreatmentOnly: false,
    includeProductOnly: false,
    resetPeriod: 'NEVER',
    autoUpgradeEnabled: true,
    autoDowngradeEnabled: false,
    notifyOnUpgrade: true,
    notifyOnDowngrade: true
  })

  // Conditions state
  const [conditions, setConditions] = useState<RankConditionInterface[]>([])
  const [selectedRankId, setSelectedRankId] = useState<number | null>(null)
  const [isConditionModalOpen, setIsConditionModalOpen] = useState(false)
  const [editingCondition, setEditingCondition] = useState<RankConditionInterface | null>(null)

  // Condition form state
  const [conditionForm, setConditionForm] = useState<Partial<RankConditionInterface>>({
    conditionType: RANK_CONDITION_TYPE.TOTAL_SPENDING,
    operator: CONDITION_OPERATOR.GTE,
    value: 0,
    periodType: RANK_PERIOD_TYPE.LIFETIME,
    isRequired: true,
    priority: 0
  })

  // Options for dropdowns - grouped by category
  const conditionTypeOptions = [
    // กลุ่มยอดเงิน
    { value: RANK_CONDITION_TYPE.TOTAL_SPENDING, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.TOTAL_SPENDING'), unit: 'บาท', group: 'ยอดเงิน' },
    { value: RANK_CONDITION_TYPE.AVERAGE_SPENDING, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.AVERAGE_SPENDING'), unit: 'บาท', group: 'ยอดเงิน' },
    { value: RANK_CONDITION_TYPE.SINGLE_RECEIPT, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.SINGLE_RECEIPT'), unit: 'บาท', group: 'ยอดเงิน' },
    { value: RANK_CONDITION_TYPE.PRODUCT_PURCHASE, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.PRODUCT_PURCHASE'), unit: 'บาท', group: 'ยอดเงิน' },
    { value: RANK_CONDITION_TYPE.TREATMENT_SPENDING, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.TREATMENT_SPENDING'), unit: 'บาท', group: 'ยอดเงิน' },
    
    // กลุ่มการมาใช้บริการ
    { value: RANK_CONDITION_TYPE.VISIT_COUNT, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.VISIT_COUNT'), unit: 'ครั้ง', group: 'การมาใช้บริการ' },
    { value: RANK_CONDITION_TYPE.CONSECUTIVE_VISITS, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.CONSECUTIVE_VISITS'), unit: 'ครั้ง', group: 'การมาใช้บริการ' },
    { value: RANK_CONDITION_TYPE.LAST_VISIT_DAYS, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.LAST_VISIT_DAYS'), unit: 'วัน', group: 'การมาใช้บริการ' },
    { value: RANK_CONDITION_TYPE.APPOINTMENT_KEPT, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.APPOINTMENT_KEPT'), unit: '%', group: 'การมาใช้บริการ' },
    
    // กลุ่มหัตถการ
    { value: RANK_CONDITION_TYPE.TREATMENT_TYPE, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.TREATMENT_TYPE'), unit: 'ครั้ง', group: 'หัตถการ' },
    { value: RANK_CONDITION_TYPE.TREATMENT_COURSE, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.TREATMENT_COURSE'), unit: 'คอร์ส', group: 'หัตถการ' },
    { value: RANK_CONDITION_TYPE.SPECIFIC_TREATMENT, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.SPECIFIC_TREATMENT'), unit: 'ครั้ง', group: 'หัตถการ' },
    
    // กลุ่มอื่นๆ
    { value: RANK_CONDITION_TYPE.REFERRAL_COUNT, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.REFERRAL_COUNT'), unit: 'คน', group: 'อื่นๆ' },
    { value: RANK_CONDITION_TYPE.MEMBERSHIP_DURATION, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.MEMBERSHIP_DURATION'), unit: 'เดือน', group: 'อื่นๆ' },
    { value: RANK_CONDITION_TYPE.POINTS_BALANCE, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.POINTS_BALANCE'), unit: 'คะแนน', group: 'อื่นๆ' },
    { value: RANK_CONDITION_TYPE.BIRTHDAY_MONTH, label: t('MEMBER.RANK_UPDATE.CONDITION_TYPES.BIRTHDAY_MONTH'), unit: 'เดือน', group: 'อื่นๆ' }
  ]

  const periodTypeOptions = [
    { value: RANK_PERIOD_TYPE.LIFETIME, label: t('MEMBER.RANK_UPDATE.PERIOD_TYPES.LIFETIME') },
    { value: RANK_PERIOD_TYPE.YEARLY, label: t('MEMBER.RANK_UPDATE.PERIOD_TYPES.YEARLY') },
    { value: RANK_PERIOD_TYPE.QUARTERLY, label: t('MEMBER.RANK_UPDATE.PERIOD_TYPES.QUARTERLY') },
    { value: RANK_PERIOD_TYPE.MONTHLY, label: t('MEMBER.RANK_UPDATE.PERIOD_TYPES.MONTHLY') },
    { value: RANK_PERIOD_TYPE.CUSTOM, label: t('MEMBER.RANK_UPDATE.PERIOD_TYPES.CUSTOM') }
  ]

  const calculationModeOptions = [
    { value: CALCULATION_MODE.REALTIME, label: t('MEMBER.RANK_UPDATE.CALCULATION_MODES.REALTIME') },
    { value: CALCULATION_MODE.DAILY, label: t('MEMBER.RANK_UPDATE.CALCULATION_MODES.DAILY') },
    { value: CALCULATION_MODE.WEEKLY, label: t('MEMBER.RANK_UPDATE.CALCULATION_MODES.WEEKLY') },
    { value: CALCULATION_MODE.MONTHLY, label: t('MEMBER.RANK_UPDATE.CALCULATION_MODES.MONTHLY') }
  ]

  const resetPeriodOptions = [
    { value: 'NEVER', label: t('MEMBER.RANK_UPDATE.RESET_PERIODS.NEVER') },
    { value: 'YEARLY', label: t('MEMBER.RANK_UPDATE.RESET_PERIODS.YEARLY') },
    { value: 'FISCAL_YEAR', label: t('MEMBER.RANK_UPDATE.RESET_PERIODS.FISCAL_YEAR') }
  ]

  const operatorOptions = [
    { value: '>=', label: '>= (มากกว่าหรือเท่ากับ)' },
    { value: '>', label: '> (มากกว่า)' },
    { value: '=', label: '= (เท่ากับ)' },
    { value: '<=', label: '<= (น้อยกว่าหรือเท่ากับ)' },
    { value: '<', label: '< (น้อยกว่า)' }
  ]

  // Get description for each condition type
  const getConditionDescription = (type: RANK_CONDITION_TYPE): string => {
    const descriptions: Record<string, string> = {
      [RANK_CONDITION_TYPE.TOTAL_SPENDING]: 'ยอดใช้จ่ายรวมทั้งหมดของผู้ป่วยในช่วงเวลาที่กำหนด',
      [RANK_CONDITION_TYPE.AVERAGE_SPENDING]: 'ยอดใช้จ่ายเฉลี่ยต่อครั้งที่มาใช้บริการ',
      [RANK_CONDITION_TYPE.SINGLE_RECEIPT]: 'ยอดขั้นต่ำของใบเสร็จเดียว (เคยจ่ายครั้งเดียว >= X บาท)',
      [RANK_CONDITION_TYPE.PRODUCT_PURCHASE]: 'ยอดซื้อสินค้า/ผลิตภัณฑ์รวม',
      [RANK_CONDITION_TYPE.TREATMENT_SPENDING]: 'ยอดค่าหัตถการรวม (ไม่รวมสินค้า)',
      [RANK_CONDITION_TYPE.VISIT_COUNT]: 'จำนวนครั้งที่มาใช้บริการ',
      [RANK_CONDITION_TYPE.CONSECUTIVE_VISITS]: 'จำนวนครั้งที่มาใช้บริการติดต่อกัน (ไม่ขาดนัด)',
      [RANK_CONDITION_TYPE.LAST_VISIT_DAYS]: 'จำนวนวันนับจากวันที่มาล่าสุด (ต้องไม่เกินกี่วัน)',
      [RANK_CONDITION_TYPE.APPOINTMENT_KEPT]: 'เปอร์เซ็นต์การรักษานัดหมาย (มา/นัด x 100)',
      [RANK_CONDITION_TYPE.TREATMENT_TYPE]: 'จำนวนครั้งที่ทำหัตถการประเภทใดก็ได้',
      [RANK_CONDITION_TYPE.TREATMENT_COURSE]: 'จำนวนคอร์สหัตถการที่ซื้อ',
      [RANK_CONDITION_TYPE.SPECIFIC_TREATMENT]: 'จำนวนครั้งที่ทำหัตถการเฉพาะที่กำหนด',
      [RANK_CONDITION_TYPE.REFERRAL_COUNT]: 'จำนวนคนที่แนะนำมาใช้บริการ',
      [RANK_CONDITION_TYPE.MEMBERSHIP_DURATION]: 'ระยะเวลาที่เป็นสมาชิก (เดือน)',
      [RANK_CONDITION_TYPE.POINTS_BALANCE]: 'ยอดคะแนนสะสมคงเหลือ',
      [RANK_CONDITION_TYPE.BIRTHDAY_MONTH]: 'เดือนเกิดของผู้ป่วย (1-12)'
    }
    return descriptions[type] || 'เงื่อนไขสำหรับการอัพเกรดระดับสมาชิก'
  }

  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      const res = await MemberRankApi.getSettings()
      if (res.status === 200 && res.data) {
        setSettings(res.data)
      }
    } catch (error) {
      console.error('Error loading settings:', error)
    }
  }, [])

  // Load conditions for selected rank
  const loadConditions = useCallback(async () => {
    if (!selectedRankId) {
      setConditions([])
      return
    }
    try {
      const res = await MemberRankApi.getConditions(selectedRankId)
      if (res.status === 200) {
        // Handle different response formats
        const data = res.data
        if (Array.isArray(data)) {
          setConditions(data)
        } else if (data?.rows && Array.isArray(data.rows)) {
          setConditions(data.rows)
        } else if (data?.data && Array.isArray(data.data)) {
          setConditions(data.data)
        } else {
          setConditions([])
        }
      } else {
        setConditions([])
      }
    } catch (error) {
      console.error('Error loading conditions:', error)
      setConditions([])
    }
  }, [selectedRankId])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  useEffect(() => {
    loadConditions()
  }, [loadConditions])

  // Save settings
  const handleSaveSettings = async () => {
    try {
      await MemberRankApi.updateSettings(settings)
      notiSuccess(t('MEMBER.RANK_UPDATE.SUCCESS.SETTINGS_SAVED'))
      onSettingsChange?.()
    } catch (error) {
      notiError(t('MEMBER.RANK_UPDATE.ERROR.SETTINGS_FAILED'))
    }
  }

  // Save condition
  const handleSaveCondition = async () => {
    if (!selectedRankId || !conditionForm.value) {
      return
    }

    try {
      const payload: RankConditionInterface = {
        rankId: selectedRankId,
        conditionType: conditionForm.conditionType!,
        operator: conditionForm.operator,
        value: Number(conditionForm.value),
        periodType: conditionForm.periodType,
        isRequired: conditionForm.isRequired,
        priority: conditionForm.priority
      }

      if (editingCondition?.rankConditionId) {
        await MemberRankApi.updateCondition(editingCondition.rankConditionId, payload)
        notiSuccess(t('MEMBER.RANK_UPDATE.SUCCESS.CONDITION_UPDATED'))
      } else {
        await MemberRankApi.createCondition(payload)
        notiSuccess(t('MEMBER.RANK_UPDATE.SUCCESS.CONDITION_CREATED'))
      }

      setIsConditionModalOpen(false)
      resetConditionForm()
      loadConditions()
    } catch (error) {
      notiError(t('MEMBER.RANK_UPDATE.ERROR.CONDITION_FAILED'))
    }
  }

  // Delete condition
  const handleDeleteCondition = async (conditionId: number) => {
    try {
      await MemberRankApi.deleteCondition(conditionId)
      notiSuccess(t('MEMBER.RANK_UPDATE.SUCCESS.CONDITION_DELETED'))
      loadConditions()
    } catch (error) {
      notiError(t('MEMBER.RANK_UPDATE.ERROR.CONDITION_FAILED'))
    }
  }

  // Edit condition
  const handleEditCondition = (condition: RankConditionInterface) => {
    setEditingCondition(condition)
    setConditionForm({
      conditionType: condition.conditionType,
      operator: condition.operator,
      value: condition.value,
      periodType: condition.periodType,
      isRequired: condition.isRequired,
      priority: condition.priority
    })
    setIsConditionModalOpen(true)
  }

  // Reset condition form
  const resetConditionForm = () => {
    setEditingCondition(null)
    setConditionForm({
      conditionType: RANK_CONDITION_TYPE.TOTAL_SPENDING,
      operator: CONDITION_OPERATOR.GTE,
      value: 0,
      periodType: RANK_PERIOD_TYPE.LIFETIME,
      isRequired: true,
      priority: 0
    })
  }

  // Table head cells for conditions
  const conditionHeadCells = [
    { id: 'no', label: '#', width: '50px' },
    { id: 'conditionType', label: t('MEMBER.RANK_UPDATE.CONDITION_TYPE') },
    { id: 'operator', label: t('MEMBER.RANK_UPDATE.CONDITION_OPERATOR') },
    { id: 'value', label: t('MEMBER.RANK_UPDATE.CONDITION_VALUE') },
    { id: 'periodType', label: t('MEMBER.RANK_UPDATE.PERIOD_TYPE') },
    { id: 'isRequired', label: t('MEMBER.RANK_UPDATE.IS_REQUIRED') },
    { id: 'action', label: 'Action', width: '100px' }
  ]

  // Render condition row
  const renderConditionRow = (condition: RankConditionInterface, index: number) => {
    const conditionTypeOption = conditionTypeOptions.find(o => o.value === condition.conditionType)
    const conditionTypeLabel = conditionTypeOption?.label || condition.conditionType
    const conditionUnit = conditionTypeOption?.unit || ''
    const periodTypeLabel = periodTypeOptions.find(o => o.value === condition.periodType)?.label || condition.periodType

    return (
      <TableRowCommon
        key={condition.rankConditionId ?? `new-${index}`}
        id={condition.rankConditionId ?? index}
        obj={condition}
        columns={[
          { option: 'TEXT', align: 'center', label: index + 1 },
          { option: 'TEXT', align: 'left', label: conditionTypeLabel },
          { option: 'TEXT', align: 'center', label: condition.operator },
          { option: 'TEXT', align: 'right', label: `${condition.value?.toLocaleString() || 0} ${conditionUnit}` },
          { option: 'TEXT', align: 'left', label: periodTypeLabel },
          {
            option: 'COMPONENT',
            align: 'center',
            component: <InputCheckbox checked={condition.isRequired} disabled />
          },
          {
            option: 'ACTION',
            align: 'center',
            label: 'action',
            values: [
              { option: 'EDIT', label: t('BUTTON.EDIT') },
              { option: 'DELETE', label: t('BUTTON.DELETE') }
            ]
          }
        ]}
        onedit={() => handleEditCondition(condition)}
        ondelete={() => handleDeleteCondition(condition.rankConditionId!)}
      />
    )
  }

  return (
    <Box>
      {/* Section: Calculation Settings */}
      <Box sx={{ padding: '24px', backgroundColor: colors.extraLightBlue, borderRadius: '8px', marginBottom: '24px' }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
          {t('MEMBER.RANK_UPDATE.CALCULATION_SETTINGS')}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SelectInput
              label={t('MEMBER.RANK_UPDATE.CALCULATION_MODE')}
              options={calculationModeOptions.map(o => ({ name: o.label, value: o.value }))}
              value={settings.calculationMode}
              onChange={(e: any) => setSettings({ ...settings, calculationMode: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SelectInput
              label={t('MEMBER.RANK_UPDATE.RESET_PERIOD')}
              options={resetPeriodOptions.map(o => ({ name: o.label, value: o.value }))}
              value={settings.resetPeriod}
              onChange={(e: any) => setSettings({ ...settings, resetPeriod: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoUpgradeEnabled}
                  onChange={(e) => setSettings({ ...settings, autoUpgradeEnabled: e.target.checked })}
                  color="primary"
                />
              }
              label={t('MEMBER.RANK_UPDATE.AUTO_UPGRADE_ENABLED')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoDowngradeEnabled}
                  onChange={(e) => setSettings({ ...settings, autoDowngradeEnabled: e.target.checked })}
                  color="primary"
                />
              }
              label={t('MEMBER.RANK_UPDATE.AUTO_DOWNGRADE_ENABLED')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.notifyOnUpgrade}
                  onChange={(e) => setSettings({ ...settings, notifyOnUpgrade: e.target.checked })}
                  color="primary"
                />
              }
              label={t('MEMBER.RANK_UPDATE.NOTIFY_ON_UPGRADE')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.notifyOnDowngrade}
                  onChange={(e) => setSettings({ ...settings, notifyOnDowngrade: e.target.checked })}
                  color="primary"
                />
              }
              label={t('MEMBER.RANK_UPDATE.NOTIFY_ON_DOWNGRADE')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.includeOverdue}
                  onChange={(e) => setSettings({ ...settings, includeOverdue: e.target.checked })}
                  color="primary"
                />
              }
              label={t('MEMBER.RANK_UPDATE.INCLUDE_OVERDUE')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.includeTreatmentOnly}
                  onChange={(e) => setSettings({ ...settings, includeTreatmentOnly: e.target.checked })}
                  color="primary"
                />
              }
              label={t('MEMBER.RANK_UPDATE.INCLUDE_TREATMENT_ONLY')}
            />
          </Grid>
        </Grid>

        <Box sx={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonCustom
            textButton={t('BUTTON.SAVE')}
            onClick={handleSaveSettings}
          />
        </Box>
      </Box>

      <Divider sx={{ marginBottom: '24px' }} />

      {/* Section: Rank Conditions */}
      <Box sx={{ padding: '24px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
            {t('MEMBER.RANK_UPDATE.RANK_CONDITIONS')}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '14px', color: colors.disabledGray, marginBottom: '16px' }}>
          กำหนดเงื่อนไขสำหรับการอัพเกรดระดับสมาชิกอัตโนมัติ เช่น ยอดใช้จ่ายขั้นต่ำ, จำนวนครั้งที่มาใช้บริการ, จำนวนการแนะนำ เป็นต้น
        </Typography>

        {/* Rank selector */}
        <Grid container spacing={2} sx={{ marginBottom: '16px' }}>
          <Grid item xs={12} md={4}>
            <AutocompleteSelect
              labelId="select-rank"
              inputLabel="เลือกระดับสมาชิก"
              options={ranks}
              getOptionLabel={(option: any) => option?.rankNameEn || ''}
              value={ranks.find((r: any) => r.rankId === selectedRankId) || null}
              onchange={(e: any, value: any) => setSelectedRankId(value?.rankId || null)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ButtonCustom
              variant="outlined"
              startIcon={<AddCircleIcon />}
              onClick={() => {
                resetConditionForm()
                setIsConditionModalOpen(true)
              }}
              textButton={t('MEMBER.RANK_UPDATE.ADD_CONDITION')}
              disabled={!selectedRankId}
            />
          </Grid>
        </Grid>

        {/* Condition Logic Info */}
        {selectedRankId && Array.isArray(conditions) && conditions.length > 1 && (
          <Box sx={{
            backgroundColor: colors.extraLightBlue,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Typography sx={{ fontSize: '14px' }}>
              💡 เงื่อนไขทั้งหมดจะถูกคำนวณแบบ <strong>AND</strong> (ต้องผ่านทุกเงื่อนไขที่กำหนดเป็น &quot;จำเป็น&quot;)
            </Typography>
          </Box>
        )}

        {/* Conditions table */}
        {selectedRankId && Array.isArray(conditions) && (
          <TableCustom
            page={1}
            pageLimit={10}
            sortType="ASC"
            sortBy="priority"
            onSort={() => { /* no sorting needed */ }}
            setPageLimit={() => { /* pagination disabled */ }}
            setPage={() => { /* pagination disabled */ }}
            rowCount={conditions.length}
            headCells={conditionHeadCells}
            rowsData={conditions.map((c, i) => renderConditionRow(c, i))}
            hidePagination
          />
        )}

        {!selectedRankId && (
          <Box sx={{ textAlign: 'center', padding: '40px', color: colors.disabledGray }}>
            <Typography>กรุณาเลือกระดับสมาชิกเพื่อดูและจัดการเงื่อนไข</Typography>
          </Box>
        )}
      </Box>

      {/* Condition Modal */}
      <PageModal
        isOpen={isConditionModalOpen}
        title={editingCondition ? 'แก้ไขเงื่อนไข' : t('MEMBER.RANK_UPDATE.ADD_CONDITION')}
        size="M"
        handleClose={() => {
          setIsConditionModalOpen(false)
          resetConditionForm()
        }}
        onCancel={{
          onClick: () => {
            setIsConditionModalOpen(false)
            resetConditionForm()
          },
          title: t('BUTTON.CANCEL')
        }}
        onSubmit={{ onClick: handleSaveCondition, title: t('BUTTON.SAVE') }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <SelectInput
            label={t('MEMBER.RANK_UPDATE.CONDITION_TYPE')}
            options={conditionTypeOptions.map(o => ({ name: `${o.label} (${o.group})`, value: o.value }))}
            value={conditionForm.conditionType}
            onChange={(e: any) => {
              const newType = e.target.value
              // Auto-select appropriate operator based on condition type
              let defaultOperator: CONDITION_OPERATOR = CONDITION_OPERATOR.GTE
              if (newType === RANK_CONDITION_TYPE.LAST_VISIT_DAYS) {
                defaultOperator = CONDITION_OPERATOR.LTE // วันที่มาล่าสุดต้องน้อยกว่าหรือเท่ากับ
              } else if (newType === RANK_CONDITION_TYPE.BIRTHDAY_MONTH) {
                defaultOperator = CONDITION_OPERATOR.EQ // เดือนเกิดต้องเท่ากับ
              }
              setConditionForm({ ...conditionForm, conditionType: newType, operator: defaultOperator })
            }}
          />

          {/* Condition Description */}
          <Box sx={{ backgroundColor: colors.extraLightGray, borderRadius: '4px', padding: '8px 12px' }}>
            <Typography sx={{ fontSize: '13px', color: colors.disabledGray }}>
              💡 {getConditionDescription(conditionForm.conditionType as RANK_CONDITION_TYPE)}
            </Typography>
          </Box>

          <SelectInput
            label={t('MEMBER.RANK_UPDATE.CONDITION_OPERATOR')}
            options={operatorOptions.map(o => ({ name: o.label, value: o.value }))}
            value={conditionForm.operator}
            onChange={(e: any) => setConditionForm({ ...conditionForm, operator: e.target.value })}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label={t('MEMBER.RANK_UPDATE.CONDITION_VALUE')}
                value={conditionForm.value?.toString() || ''}
                onChange={(e: any) => {
                  const val = e.target.value
                  if (!isNaN(val)) {
                    setConditionForm({ ...conditionForm, value: Number(val) })
                  }
                }}
                type="number"
                fullWidth
                required
              />
            </Box>
            <Typography sx={{ minWidth: '60px', color: colors.disabledGray }}>
              {conditionTypeOptions.find(o => o.value === conditionForm.conditionType)?.unit || ''}
            </Typography>
          </Box>

          <SelectInput
            label={t('MEMBER.RANK_UPDATE.PERIOD_TYPE')}
            options={periodTypeOptions.map(o => ({ name: o.label, value: o.value }))}
            value={conditionForm.periodType}
            onChange={(e: any) => setConditionForm({ ...conditionForm, periodType: e.target.value })}
          />

          {conditionForm.periodType === RANK_PERIOD_TYPE.CUSTOM && (
            <TextInput
              label="จำนวนเดือน"
              value={conditionForm.periodMonths?.toString() || ''}
              onChange={(e: any) => {
                const val = e.target.value
                if (!isNaN(val)) {
                  setConditionForm({ ...conditionForm, periodMonths: Number(val) })
                }
              }}
              type="number"
              fullWidth
            />
          )}

          <TextInput
            label={t('MEMBER.RANK_UPDATE.PRIORITY')}
            value={conditionForm.priority?.toString() || '0'}
            onChange={(e: any) => {
              const val = e.target.value
              if (!isNaN(val)) {
                setConditionForm({ ...conditionForm, priority: Number(val) })
              }
            }}
            type="number"
            fullWidth
            helperText="ลำดับความสำคัญ (ตัวเลขน้อย = สำคัญกว่า)"
          />

          <FormControlLabel
            control={
              <Switch
                checked={conditionForm.isRequired}
                onChange={(e) => setConditionForm({ ...conditionForm, isRequired: e.target.checked })}
                color="primary"
              />
            }
            label={`${t('MEMBER.RANK_UPDATE.IS_REQUIRED')} (ต้องผ่านเงื่อนไขนี้จึงจะอัพเกรด)`}
          />
        </Box>
      </PageModal>
    </Box>
  )
}
